/**
 * Unified picker for compliance / onboarding uploads.
 *
 * Lets the caller offer the nurse three sources — Camera, Photo library,
 * PDF — and returns a normalized `{ uri, contentType, filename }` shape
 * so call sites don't have to branch on which picker fired.
 *
 * Identity Verification (selfie + ID) stays on the raw `ImagePicker` calls
 * because that flow is image-only by design (the face-comparison pipeline
 * expects images, not PDFs).
 */
import * as DocumentPicker from "expo-document-picker";
import { manipulateAsync, SaveFormat } from "expo-image-manipulator";
import * as ImagePicker from "expo-image-picker";

import { MOBILE_DEBUG_VERSION } from "@/lib/debug";

/**
 * Normalize a picked image to JPEG.
 *
 * iPhones default to HEIC for the camera roll (Apple's native format since
 * iOS 11), but our server-side combiner only knows JPEG/PNG/PDF — and
 * teaching pdf-lib to decode HEIC would mean shipping libheif into the
 * Node container. Cheaper to convert on the phone where the native image
 * APIs read HEIC for free via `manipulateAsync`. Already-JPEG images go
 * through the same path; the quality cap matches the picker's `quality: 0.8`
 * so we don't double-compress.
 *
 * Returns the JPEG `uri` plus the filename rewritten to `.jpg`. The
 * underlying file lives in the manipulator's cache directory and stays
 * readable until the app process restarts.
 */
async function toJpeg(
    asset: ImagePicker.ImagePickerAsset,
    idx: number
): Promise<{
    uri: string;
    filename: string;
}> {
    const out = await manipulateAsync(asset.uri, [], {
        compress: 0.8,
        format: SaveFormat.JPEG
    });
    // Strip any extension off the original name and force `.jpg` so the
    // S3 key + admin doc viewer agree on the format.
    const base = (asset.fileName ?? `photo-${Date.now()}-${idx}`).replace(/\.[^.]+$/, "");
    return { uri: out.uri, filename: `${base}.jpg` };
}

/** Normalized upload-source result. */
export interface PickedUpload {
    uri: string;
    contentType: string;
    filename: string;
}

/**
 * Outcome of a picker call.
 *
 *   "picked"   — user selected one or more files, returned in `value`.
 *   "cancelled"— user backed out of the picker; show nothing.
 *   "denied"   — OS-level permission denied; surface a one-line hint.
 *
 * `value` is always an array so multi-select pickers (photo library, PDF
 * picker) can return all chosen files in one call. Camera is single-shot
 * but returns a 1-element array for API parity. Distinguishing cancelled
 * vs denied matters because we only nag about Settings on a real denial.
 */
export type PickResult =
    { status: "picked"; value: PickedUpload[] } | { status: "cancelled" } | { status: "denied" };

/**
 * Open the system camera, return a single captured image.
 * Requests camera permission on first use.
 */
export async function pickFromCamera(): Promise<PickResult> {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) {
        return { status: "denied" };
    }
    const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"],
        quality: 0.8
    });
    if (result.canceled || result.assets.length === 0) {
        return { status: "cancelled" };
    }
    const asset = result.assets[0];
    // Force JPEG even for the camera path — quality < 1 usually does this,
    // but Live Photos / certain capture modes can still emit HEIC.
    const jpeg = await toJpeg(asset, 0);
    return {
        status: "picked",
        value: [
            {
                uri: jpeg.uri,
                contentType: "image/jpeg",
                filename: jpeg.filename
            }
        ]
    };
}

/**
 * Open the system photo library with multi-select enabled — the nurse
 * picks every page of the document in one shot. Cap at 10 to match the
 * server-side multipart limit (`files: 10` in server.ts).
 */
export async function pickFromPhotoLibrary(): Promise<PickResult> {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
        return { status: "denied" };
    }
    const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        quality: 0.8,
        allowsMultipleSelection: true,
        selectionLimit: 10
    });
    if (result.canceled || result.assets.length === 0) {
        return { status: "cancelled" };
    }
    // Convert each picked image to JPEG sequentially. HEIC is what modern
    // iPhones save by default; the combiner doesn't know HEIC. Sequential
    // (not Promise.all) so we don't fan out N native bridge calls at once
    // on lower-end devices.
    const value: PickedUpload[] = [];
    for (let i = 0; i < result.assets.length; i++) {
        const jpeg = await toJpeg(result.assets[i], i);
        value.push({ uri: jpeg.uri, contentType: "image/jpeg", filename: jpeg.filename });
    }
    return { status: "picked", value };
}

// expo-document-picker holds a single native picker session at a time. A
// second concurrent call (a fast double-tap, or two call sites racing —
// compliance upload, chat attachments, and the generic upload flow all call
// this) throws "Different document picking in progress. Await other document
// picking first" instead of resolving, and most call sites don't wrap the
// await in try/catch, so it surfaces as an unhandled rejection
// (TASKHEALTH-MOBILE-4). Guard here, once, so every caller is covered.
let pdfPickInFlight = false;

/**
 * Open the system file picker scoped to PDFs. Multi-select enabled so a
 * nurse can grab several scanned PDFs at once. No permission gate exists
 * for the document picker on iOS or Android — this never returns "denied".
 */
export async function pickPdfDocument(): Promise<PickResult> {
    if (pdfPickInFlight) {
        return { status: "cancelled" };
    }
    pdfPickInFlight = true;
    try {
        const result = await DocumentPicker.getDocumentAsync({
            type: "application/pdf",
            // We copy so the underlying file URL stays readable after the picker
            // dismisses — DocumentPicker hands back a temp URI by default.
            copyToCacheDirectory: true,
            multiple: true
        });
        if (result.canceled || result.assets.length === 0) {
            return { status: "cancelled" };
        }
        console.info(`[mobile ${MOBILE_DEBUG_VERSION}] pdfs picked`, {
            count: result.assets.length
        });
        return {
            status: "picked",
            value: result.assets.map((asset, i) => ({
                uri: asset.uri,
                contentType: asset.mimeType ?? "application/pdf",
                // Some Android pickers hand back `null` for `name`. Fall back so the
                // S3 key + DB row are always populated.
                filename: asset.name ?? `document-${Date.now()}-${i}.pdf`
            }))
        };
    } finally {
        pdfPickInFlight = false;
    }
}
